/**
 * @author เต้ย
 * @description Command Palette — Ctrl+K launcher (ticket + commands)
 */
/* eslint-disable no-redeclare */
class CommandPalette {
    constructor({ axiosClient, toast, helper }) {
        this._axiosClient = axiosClient;
        this._moduleToast = toast;
        this._moduleHelper = helper;

        this.rootId = 'command-palette-root';
        this.isOpen = false;
        this.results = { tickets: [], members: [], labels: [], states: [] };
        this.flatItems = [];
        this.activeIndex = 0;
        this.searchDebounce = null;
        this.lastQuery = '';
        this.cache = new Map();

        this._ensureRoot();
        this._bindGlobalKeys();
    }

    _ensureRoot() {
        if (document.getElementById(this.rootId)) return;
        const root = document.createElement('div');
        root.id = this.rootId;
        root.className = 'cmd-palette-overlay d-none';
        root.innerHTML = `
            <div class="cmd-palette-backdrop"></div>
            <div class="cmd-palette-modal card shadow-lg">
                <div class="cmd-palette-input-wrap">
                    <i class="fa-solid fa-magnifying-glass"></i>
                    <input type="text" class="cmd-palette-input" placeholder="ค้นหา ticket หรือพิมพ์คำสั่ง..." autocomplete="off" />
                    <span class="badge bg-light text-dark">Esc</span>
                </div>
                <div class="cmd-palette-results"></div>
                <div class="cmd-palette-footer">
                    <span><kbd>↑↓</kbd> เลือก</span>
                    <span><kbd>Enter</kbd> ไปต่อ</span>
                    <span><kbd>Esc</kbd> ปิด</span>
                </div>
            </div>
        `;
        document.body.appendChild(root);

        this.$root = root;
        this.$input = root.querySelector('.cmd-palette-input');
        this.$results = root.querySelector('.cmd-palette-results');
        this.$backdrop = root.querySelector('.cmd-palette-backdrop');

        this.$backdrop.addEventListener('click', () => this.close());
        this.$input.addEventListener('input', (e) => this._onInput(e.target.value));
        this.$input.addEventListener('keydown', (e) => this._onKeydown(e));
    }

    _bindGlobalKeys() {
        document.addEventListener(
            'keydown',
            (e) => {
                const isCtrlK = (e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K');
                if (isCtrlK) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.toggle();
                    return;
                }
                if (this.isOpen && e.key === 'Escape') {
                    e.preventDefault();
                    this.close();
                }
            },
            true,
        );

        // Navbar trigger button
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('#btn-open-palette');
            if (btn) {
                e.preventDefault();
                this.toggle();
            }
        });
    }

    open() {
        if (this.isOpen) return;
        this.isOpen = true;
        this.$root.classList.remove('d-none');
        this.$input.value = '';
        this.lastQuery = '';
        this._search('');
        setTimeout(() => this.$input.focus(), 20);
    }

    close() {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.$root.classList.add('d-none');
    }

    toggle() {
        if (this.isOpen) this.close();
        else this.open();
    }

    _onInput(value) {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = setTimeout(() => this._search(value), 200);
    }

    _onKeydown(e) {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this._move(1);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this._move(-1);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            this._executeActive();
        }
    }

    _move(delta) {
        if (!this.flatItems.length) return;
        this.activeIndex = (this.activeIndex + delta + this.flatItems.length) % this.flatItems.length;
        this._render();
    }

    async _search(query) {
        const q = (query || '').trim();
        if (this.cache.has(q)) {
            this.results = this.cache.get(q);
            this._buildFlatItems(q);
            this._render();
            return;
        }
        try {
            const res = await this._axiosClient.get('ticket/api/palette_search', { q, limit: 10 });
            const data = res?.data?.data || res?.data || {};
            this.results = {
                tickets: data.tickets || [],
                members: data.members || [],
                labels: data.labels || [],
                states: data.states || [],
            };
            this.cache.set(q, this.results);
            if (this.cache.size > 20) {
                const firstKey = this.cache.keys().next().value;
                this.cache.delete(firstKey);
            }
        } catch (e) {
            this.results = { tickets: [], members: [], labels: [], states: [] };
        }
        this.lastQuery = q;
        this._buildFlatItems(q);
        this._render();
    }

    _buildFlatItems(q) {
        const items = [];
        const ctx = this._currentTicketContext();

        // Commands (prefixed)
        if (q.startsWith('>')) {
            const term = q.slice(1).trim().toLowerCase();
            const cmds = [
                { label: 'หน้าหลัก — Kanban', action: () => (window.location.href = 'home') },
                { label: 'รายการ Ticket', action: () => (window.location.href = 'ticket') },
                { label: 'สร้าง Ticket ใหม่', action: () => this._openCreateModal() },
            ];
            cmds.filter((c) => !term || c.label.toLowerCase().includes(term)).forEach((c) =>
                items.push({ kind: 'command', label: c.label, action: c.action }),
            );
        } else if (q.toLowerCase().startsWith('new ticket')) {
            const title = q.slice('new ticket'.length).replace(/^[:\s]+/, '');
            items.push({
                kind: 'command',
                label: `สร้าง ticket ใหม่${title ? ` — "${title}"` : ''}`,
                action: () => this._openCreateModal(title),
            });
        } else if (ctx.ticketId && q.startsWith('@')) {
            const term = q.slice(1).trim().toLowerCase();
            (this.results.members || [])
                .filter((m) => !term || (m.name || '').toLowerCase().includes(term))
                .forEach((m) =>
                    items.push({
                        kind: 'action',
                        label: `Assign → ${m.name}`,
                        action: () =>
                            this._toast(`สำหรับ ticket เปิดอยู่ ใช้ปุ่ม Assign ใน modal โดยตรง (#${ctx.ticketId})`),
                    }),
                );
        } else if (ctx.ticketId && q.startsWith('+')) {
            const term = q.slice(1).trim().toLowerCase();
            (this.results.labels || [])
                .filter((l) => !term || (l.name || '').toLowerCase().includes(term))
                .forEach((l) =>
                    items.push({
                        kind: 'action',
                        label: `เพิ่ม tag → ${l.name}`,
                        action: () =>
                            this._toast(`สำหรับ ticket เปิดอยู่ ใช้ช่อง tag ใน modal โดยตรง (#${ctx.ticketId})`),
                    }),
                );
        } else if (ctx.ticketId && (q.startsWith('→') || q.toLowerCase().startsWith('state '))) {
            const term = q
                .replace(/^(→|state)\s*/i, '')
                .trim()
                .toLowerCase();
            (this.results.states || [])
                .filter((s) => {
                    const name = (s.tag_name || s.name || '').toLowerCase();
                    return !term || name.includes(term);
                })
                .forEach((s) =>
                    items.push({
                        kind: 'action',
                        label: `เปลี่ยน state → ${s.tag_name || s.name}`,
                        action: () => this._toast(`เปลี่ยน state ผ่าน dropdown ใน modal (#${ctx.ticketId})`),
                    }),
                );
        }

        // Default: tickets from search
        (this.results.tickets || []).forEach((t) =>
            items.push({
                kind: 'ticket',
                label: `#${t.ticket_id} ${t.ticket_name}`,
                sublabel: t.state_name || '',
                color: t.state_color || '#6c757d',
                action: () => this._openTicket(t.ticket_id),
            }),
        );

        // If nothing typed → quick actions
        if (!q) {
            items.unshift({ kind: 'command', label: '✚ สร้าง Ticket ใหม่', action: () => this._openCreateModal() });
        }

        this.flatItems = items;
        this.activeIndex = 0;
    }

    _render() {
        if (!this.flatItems.length) {
            this.$results.innerHTML = `<div class="cmd-palette-empty">ไม่มีผลลัพธ์</div>`;
            return;
        }
        const html = this.flatItems
            .map((item, idx) => {
                const active = idx === this.activeIndex ? 'active' : '';
                const icon = item.kind === 'ticket' ? '🎫' : item.kind === 'command' ? '⚡' : '✏️';
                const sublabel = item.sublabel
                    ? `<span class="cmd-palette-sublabel" style="background:${item.color || '#6c757d'}">${this._escape(item.sublabel)}</span>`
                    : '';
                return `
                    <div class="cmd-palette-item ${active}" data-idx="${idx}">
                        <span class="cmd-palette-icon">${icon}</span>
                        <span class="cmd-palette-label">${this._escape(item.label)}</span>
                        ${sublabel}
                    </div>
                `;
            })
            .join('');
        this.$results.innerHTML = html;

        this.$results.querySelectorAll('.cmd-palette-item').forEach((el) => {
            el.addEventListener('mousedown', (e) => {
                e.preventDefault();
                this.activeIndex = parseInt(el.dataset.idx, 10);
                this._executeActive();
            });
            el.addEventListener('mouseenter', () => {
                this.activeIndex = parseInt(el.dataset.idx, 10);
                this.$results
                    .querySelectorAll('.cmd-palette-item.active')
                    .forEach((e) => e.classList.remove('active'));
                el.classList.add('active');
            });
        });

        const active = this.$results.querySelector('.cmd-palette-item.active');
        if (active) active.scrollIntoView({ block: 'nearest' });
    }

    _executeActive() {
        const item = this.flatItems[this.activeIndex];
        if (!item) return;
        try {
            item.action();
        } finally {
            this.close();
        }
    }

    _openTicket(ticketId) {
        const page = window.ticketInstance;
        if (page && typeof page.showEditTicketModal === 'function') {
            page.showEditTicketModal(ticketId);
            return;
        }
        window.location.href = `ticket?ticket_id=${ticketId}`;
    }

    _openCreateModal(prefillTitle) {
        const modal = document.getElementById('modal-add-ticket');
        if (!modal) {
            window.location.href = 'ticket';
            return;
        }
        const bsModal = bootstrap.Modal.getOrCreateInstance(modal);
        bsModal.show();
        if (prefillTitle) {
            setTimeout(() => {
                const nameInput = document.querySelector('#modal-add-ticket input[name="ticket_name"]');
                if (nameInput) nameInput.value = prefillTitle;
            }, 250);
        }
    }

    _currentTicketContext() {
        const modal = document.getElementById('modal-add-ticket');
        if (!modal || !modal.classList.contains('show')) return { ticketId: null };
        const id = (window.Ticket && window.Ticket.currentTicketId) || null;
        return { ticketId: id };
    }

    _toast(msg) {
        if (this._moduleToast && this._moduleToast.info) this._moduleToast.info(msg);
        else console.info(msg);
    }

    _escape(str) {
        if (!str) return '';
        if (this._moduleHelper && this._moduleHelper.escapeHtml) return this._moduleHelper.escapeHtml(str);
        return String(str).replace(
            /[&<>"']/g,
            (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c],
        );
    }
}

if (typeof window !== 'undefined') {
    window.CommandPalette = CommandPalette;
}
